import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { UserPlus, Upload, Search, ToggleLeft, ToggleRight, X, Stethoscope, FileText, Eye, Download } from 'lucide-react';
import {
  getPlayers, createPlayer, updatePlayer, getPlayerPositions, createPlayerPosition, deletePlayerPosition,
  getInjuries, createInjury, updateInjury, deleteInjury, getMedicalOptions,
  getPlayerDocuments, documentViewUrl, documentDownloadUrl, DOCUMENT_CATEGORIES,
} from '../api';

const POSITIONS = ['GK','CB','FB','CDM','CAM','ST','W'];

// Shows one decimal place only when the value actually has one (78.4 stays 78.4, 78 stays 78).
export const fmtWeight = w => {
  if (w === null || w === undefined || w === '') return null;
  const n = Number(w);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

// All EU member-state nationalities, plus the larger non-EU footballing nations,
// alphabetically sorted with "Other" kept last as a catch-all.
export const NATIONALITIES = [
  'Albanian', 'Algerian', 'American', 'Argentinian', 'Australian', 'Austrian',
  'Belgian', 'Bosnian', 'Brazilian', 'Bulgarian',
  'Cameroonian', 'Canadian', 'Chilean', 'Chinese', 'Colombian', 'Croatian', 'Cypriot', 'Czech',
  'Danish', 'Dutch',
  'Egyptian', 'English', 'Estonian',
  'Finnish', 'French',
  'Georgian', 'German', 'Ghanaian', 'Greek',
  'Hungarian',
  'Icelandic', 'Indian', 'Irish', 'Italian', 'Ivorian',
  'Japanese',
  'Latvian', 'Lithuanian', 'Luxembourgish',
  'Maltese', 'Mexican', 'Moroccan',
  'Nigerian', 'Northern Irish', 'Norwegian',
  'Polish', 'Portuguese',
  'Romanian', 'Russian',
  'Scottish', 'Senegalese', 'Serbian', 'Slovak', 'Slovenian', 'South African', 'South Korean', 'Spanish', 'Swedish', 'Swiss',
  'Tunisian', 'Turkish',
  'Ukrainian',
  'Welsh',
  'Other',
];

const emptyPlayer = {
  name: '', squadNumber: '', position: '', nationality: '',
  dob: '', height: '', weight: '', dominant_foot: 'Right', active: true, notes: ''
};

function Avatar({ name, photoUrl, size = 40 }) {
  const [err, setErr] = React.useState(false);
  const initials = name ? name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?';
  const colors = ['#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444','#06b6d4','#ec4899'];
  const color = colors[(name?.charCodeAt(0) || 0) % colors.length];
  if (photoUrl && !err) return (
    <>
      <img src={photoUrl} alt="" onError={() => setErr(true)} style={{ display: 'none' }} />
      <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, backgroundImage: `url(${photoUrl})`, backgroundSize: 'cover', backgroundPosition: 'center top' }} />
    </>
  );
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: size * 0.35, color: '#fff', flexShrink: 0 }}>
      {initials}
    </div>
  );
}

const fmtHistoryDate = d => { if (!d) return '—'; const [y, m, day] = d.slice(0, 10).split('-'); return `${day}/${m}/${y}`; };

// Date-scoped position history (2026-07-29) — a player's players.position is a single current
// value, but some players genuinely play different positions match to match over a season
// (e.g. CB in some matches, CDM in others). Match Chronic, Match Performance, and the Full
// Physical Report all resolve the position played AS OF a given match's date from this list
// (see matchChronicShared.js's findMatchPosition) instead of always using the current value —
// a player with no entries here just keeps using their current position everywhere, unchanged.
function PositionHistorySection({ playerId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPosition, setNewPosition] = useState('');
  const [newDate, setNewDate] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getPlayerPositions()
      .then(all => {
        const mine = (all || [])
          .filter(p => String(p.player_id) === String(playerId))
          .sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''));
        setEntries(mine);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, [playerId]);

  const handleAdd = async () => {
    if (!newPosition || !newDate) return;
    setSaving(true);
    try {
      await createPlayerPosition({ player_id: playerId, position: newPosition, start_date: newDate });
      setNewPosition('');
      setNewDate('');
      load();
    } catch (err) {
      console.error('Failed to add position history entry:', err);
      alert('Failed to add position change. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async id => {
    try {
      await deletePlayerPosition(id);
      load();
    } catch (err) {
      console.error('Failed to delete position history entry:', err);
    }
  };

  const inputStyle = { padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 14, outline: 'none' };
  const labelStyle = { fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };

  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <label style={labelStyle}>Position History</label>
      <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 8 }}>
        Only needed if this player has played a different position than usual in some matches — Match Chronic,
        Match Performance, and PDF reports compare each match against whichever position applied on that date.
      </div>
      {!loading && entries.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
          {entries.map(e => (
            <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 6, background: 'var(--bg-app)' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', minWidth: 40 }}>{e.position}</span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', flex: 1 }}>from {fmtHistoryDate(e.start_date)}</span>
              <button onClick={() => handleDelete(e.id)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <select style={{ ...inputStyle, flex: 1 }} value={newPosition} onChange={e => setNewPosition(e.target.value)}>
          <option value="">Position…</option>
          {POSITIONS.map(p => <option key={p}>{p}</option>)}
        </select>
        <input style={{ ...inputStyle, flex: 1 }} type="date" value={newDate} onChange={e => setNewDate(e.target.value)} />
        <button
          disabled={!newPosition || !newDate || saving}
          onClick={handleAdd}
          style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: (newPosition && newDate) ? '#3b82f6' : 'var(--border)', color: (newPosition && newDate) ? '#fff' : 'var(--text-dimmed)', cursor: (newPosition && newDate) ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {saving ? 'Adding…' : 'Add'}
        </button>
      </div>
    </div>
  );
}

const emptyInjuryForm = {
  date_injured: '', body_part: '', injury_type: '',
  already_resolved: false, actual_return_date: '', diagnostic_notes: '',
};

// Injury History popup (2026-08-23) — separate from PlayerModal's own editing so it's reachable
// with one click from the card, not buried behind "edit player" first. Built for logging injuries
// that predate this system (or predate the player's time here), so unlike Medical.js's InjuryModal
// this skips mechanism/side/context as user-facing fields (mechanism/side/context are rarely known
// for old injuries) — they're still sent to satisfy the injuries table's NOT NULL columns, just
// with "unknown-safe" fixed values ('Other'/'N/A'/'Other') rather than a picker. Uses the same
// medical_options body_part/injury_type lists as Medical.js so terminology stays consistent
// between the two entry points, and the same hard-delete endpoint (DELETE /api/injuries/:id) the
// Medical page's "logged in error" flow added.
function InjuryHistoryModal({ player, user, onClose }) {
  const [injuries, setInjuries] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyInjuryForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([getInjuries(player.id), getMedicalOptions()])
      .then(([inj, opts]) => {
        setInjuries((inj || []).slice().sort((a, b) => (b.date_injured || '').localeCompare(a.date_injured || '')));
        setOptions(opts || []);
      })
      .catch(err => console.error('Failed to load injury history:', err))
      .finally(() => setLoading(false));
  };
  useEffect(load, [player.id]);

  const bodyParts = options.filter(o => o.category === 'body_part');
  const injTypes  = options.filter(o => o.category === 'injury_type');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const canAdd = form.date_injured && form.body_part && form.injury_type;

  const handleAdd = async () => {
    if (!canAdd) return;
    setSaving(true);
    try {
      const created = await createInjury({
        player_id: player.id,
        date_injured: form.date_injured,
        body_part: form.body_part,
        injury_type: form.injury_type,
        mechanism: 'Other',
        side: 'N/A',
        context: 'Other',
        diagnostic_notes: form.diagnostic_notes || null,
        created_by: user?.id,
      });
      if (form.already_resolved) {
        await updateInjury(created.id, {
          is_closed: true,
          current_stage: 'rtc',
          actual_return_date: form.actual_return_date || form.date_injured,
        });
      }
      setForm(emptyInjuryForm);
      setShowAdd(false);
      load();
    } catch (err) {
      console.error('Failed to add injury:', err);
      alert('Failed to add injury. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async injury => {
    if (!window.confirm(
      `Remove this injury record?\n\n${injury.body_part} — ${injury.injury_type} (${fmtHistoryDate(injury.date_injured)})\n\n` +
      `This cannot be undone.`
    )) return;
    try {
      await deleteInjury(injury.id);
      load();
    } catch (err) {
      console.error('Failed to delete injury:', err);
      alert('Failed to delete injury. Please try again.');
    }
  };

  const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 110 }}>
      <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, width: 460, maxHeight: '85vh', overflowY: 'auto', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}><X size={20} /></button>
        <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}><Stethoscope size={18} /> Injury History</h2>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>{player.name}</p>

        {loading ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '12px 0' }}>Loading…</div>
        ) : injuries.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '12px 0' }}>No injuries on record.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {injuries.map(inj => (
              <div key={inj.id} style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--bg-app)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{inj.body_part} — {inj.injury_type}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                      {fmtHistoryDate(inj.date_injured)}
                      {inj.actual_return_date ? ` → ${fmtHistoryDate(inj.actual_return_date)}` : (inj.is_closed ? '' : ' · ongoing')}
                    </div>
                    {inj.diagnostic_notes && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{inj.diagnostic_notes}</div>
                    )}
                  </div>
                  <button onClick={() => handleDelete(inj)} title="Remove"
                    style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, lineHeight: 1, flexShrink: 0 }}>×</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!showAdd ? (
          <button onClick={() => setShowAdd(true)}
            style={{ width: '100%', padding: '9px 0', borderRadius: 6, border: '1px dashed var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>
            + Add previous injury
          </button>
        ) : (
          <div style={{ borderTop: '1px solid var(--border-strong)', paddingTop: 14 }}>
            <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 10 }}>
              For injuries before this system was in use, or before the player joined the club.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={labelStyle}>Date injured *</label>
                <input style={inputStyle} type="date" value={form.date_injured} onChange={e => set('date_injured', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Body part *</label>
                <select style={inputStyle} value={form.body_part} onChange={e => set('body_part', e.target.value)}>
                  <option value="">Select…</option>
                  {bodyParts.map(o => <option key={o.id}>{o.label}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Injury type *</label>
                <select style={inputStyle} value={form.injury_type} onChange={e => set('injury_type', e.target.value)}>
                  <option value="">Select…</option>
                  {injTypes.map(o => <option key={o.id}>{o.label}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" id="already_resolved" checked={form.already_resolved} onChange={e => set('already_resolved', e.target.checked)} />
                <label htmlFor="already_resolved" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Already recovered</label>
              </div>
              {form.already_resolved && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Return date</label>
                  <input style={inputStyle} type="date" value={form.actual_return_date} onChange={e => set('actual_return_date', e.target.value)} />
                </div>
              )}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Notes</label>
                <textarea style={{ ...inputStyle, height: 56, resize: 'vertical' }} value={form.diagnostic_notes} onChange={e => set('diagnostic_notes', e.target.value)} placeholder="e.g. occurred at previous club" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowAdd(false); setForm(emptyInjuryForm); }}
                style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button disabled={!canAdd || saving} onClick={handleAdd}
                style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: canAdd ? '#3b82f6' : 'var(--border)', color: canAdd ? '#fff' : 'var(--text-dimmed)', cursor: canAdd ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600 }}>
                {saving ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Documents popup — view/download only. Assigning unmatched uploads to a player happens from the
// manual-assign queue in the Upload zone tab (DocumentUploadSection.js), not here.
function DocumentsModal({ player, onClose }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('All');

  useEffect(() => {
    setLoading(true);
    getPlayerDocuments(player.id)
      .then(docs => setDocuments(docs || []))
      .catch(err => console.error('Failed to load documents:', err))
      .finally(() => setLoading(false));
  }, [player.id]);

  const visible = categoryFilter === 'All' ? documents : documents.filter(d => d.category === categoryFilter);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 110 }}>
      <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, width: 460, maxHeight: '85vh', overflowY: 'auto', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}><X size={20} /></button>
        <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}><FileText size={18} /> Documents</h2>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>{player.name}</p>

        {documents.length > 0 && (
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            style={{ marginBottom: 12, padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontSize: 12, outline: 'none', cursor: 'pointer' }}>
            <option>All</option>
            {DOCUMENT_CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        )}

        {loading ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '12px 0' }}>Loading…</div>
        ) : visible.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '12px 0' }}>No documents on file.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {visible.map(doc => (
              <div key={doc.id} style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--bg-app)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.original_filename}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{doc.category} · {fmtHistoryDate(doc.uploaded_at)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => window.open(documentViewUrl(doc.id), '_blank')} title="View"
                      style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                      <Eye size={12} /> View
                    </button>
                    <a href={documentDownloadUrl(doc.id)} download
                      style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', textDecoration: 'none' }}>
                      <Download size={12} /> Download
                    </a>
                  </div>
                </div>
                {doc.note && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border-strong)', fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                    Note: {doc.note}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PlayerModal({ player, onSave, onClose }) {
  const [form, setForm] = useState(player || emptyPlayer);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 14, outline: 'none' };
  const labelStyle = { fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: 'var(--border)', borderRadius: 12, padding: 28, width: 520, maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}><X size={20} /></button>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 24 }}>{player?.name ? `Edit — ${player.name}` : 'Add New Player'}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 14, alignItems: 'flex-end' }}>
            <Avatar name={form.name} photoUrl={form.photoUrl || form.photo_url} size={56} />
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Photo URL</label>
              <input style={inputStyle} value={form.photoUrl ?? form.photo_url ?? ''} onChange={e => set('photoUrl', e.target.value)} placeholder="https://…" />
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Full Name *</label>
            <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Jan de Vries" />
          </div>
          <div>
            <label style={labelStyle}>Squad Number</label>
            <input style={inputStyle} type="number" value={form.squadNumber || form.squad_number || ''} onChange={e => set('squadNumber', e.target.value)} placeholder="e.g. 9" />
          </div>
          <div>
            <label style={labelStyle}>Position</label>
            <select style={inputStyle} value={form.position} onChange={e => set('position', e.target.value)}>
              <option value="">Select position</option>
              {POSITIONS.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Date of Birth</label>
            <input style={inputStyle} type="date" value={form.dob ? form.dob.slice(0,10) : ''} onChange={e => set('dob', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Nationality</label>
            <select style={inputStyle} value={form.nationality} onChange={e => set('nationality', e.target.value)}>
              <option value="">Select nationality</option>
              {NATIONALITIES.map(n => <option key={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Height (cm)</label>
            <input style={inputStyle} type="number" value={form.height || ''} onChange={e => set('height', e.target.value)} placeholder="e.g. 182" />
          </div>
          <div>
            <label style={labelStyle}>Weight (kg)</label>
            <input style={inputStyle} type="number" step="0.1" value={form.weight || ''} onChange={e => set('weight', e.target.value)} placeholder="e.g. 78.4" />
          </div>
          <div>
            <label style={labelStyle}>Dominant Foot</label>
            <select style={inputStyle} value={form.dominant_foot} onChange={e => set('dominant_foot', e.target.value)}>
              <option>Right</option><option>Left</option><option>Both</option>
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Notes</label>
            <textarea style={{ ...inputStyle, height: 72, resize: 'vertical' }} value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 12 }}>
            <label style={{ ...labelStyle, margin: 0 }}>Status</label>
            <button onClick={() => set('active', !form.active)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: form.active ? '#10b981' : '#ef4444', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, padding: 0 }}>
              {form.active ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
              {form.active ? 'Active' : 'Non-active'}
            </button>
          </div>
          {player?.id && (
            <div style={{ gridColumn: '1 / -1', paddingTop: 12, borderTop: '1px solid var(--border-strong)' }}>
              <PositionHistorySection playerId={player.id} />
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
          <button onClick={() => form.name && onSave(form)} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Save Player</button>
        </div>
      </div>
    </div>
  );
}

export default function Players({ user }) {
  const [players, setPlayers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('active');
  const [posFilter, setPosFilter] = useState('All');
  const [modal, setModal]       = useState(null);
  const [injuryModal, setInjuryModal] = useState(null);
  const [documentsModal, setDocumentsModal] = useState(null);

  // Load players from database
  useEffect(() => {
    loadPlayers();
  }, []);

  // Deep-link support: ?player_id=<id> (same convention EWMA/PlayerChronic/MaxSpeed/MatchChronic
  // already read from their own URLs) opens that player's profile modal once the roster has loaded.
  useEffect(() => {
    if (!players.length) return;
    const urlPlayerId = new URLSearchParams(window.location.search).get('player_id');
    if (!urlPlayerId) return;
    const match = players.find(p => String(p.id) === String(urlPlayerId));
    if (match) setModal(match);
  }, [players]);

  const loadPlayers = async () => {
    try {
      setLoading(true);
      const data = await getPlayers();
      setPlayers(data);
    } catch (err) {
      console.error('Failed to load players:', err);
      // Fallback to localStorage
      const saved = localStorage.getItem('heracles_players');
      if (saved) setPlayers(JSON.parse(saved));
    } finally {
      setLoading(false);
    }
  };

  const savePlayer = async form => {
    try {
      const payload = {
        name:          form.name,
        squad_number:  form.squadNumber || form.squad_number || null,
        position:      form.position || null,
        nationality:   form.nationality || null,
        dob:           form.dob || null,
        height:        form.height || null,
        weight:        form.weight || null,
        dominant_foot: form.dominant_foot || 'Right',
        max_speed:     form.maxSpeed || null,
        photo_url:     form.photoUrl || form.photo_url || null,
        active:        form.active !== false,
        notes:         form.notes || null,
      };

      if (modal === 'add') {
        const newPlayer = await createPlayer(payload);
        setPlayers(prev => [...prev, newPlayer]);
      } else {
        const updated = await updatePlayer(modal.id, payload);
        setPlayers(prev => prev.map(p => p.id === modal.id ? updated : p));
      }
      setModal(null);
    } catch (err) {
      console.error('Failed to save player:', err);
      alert('Failed to save player. Please try again.');
    }
  };

  const toggleActive = async player => {
    try {
      const updated = await updatePlayer(player.id, {
        ...player,
        squad_number: player.squad_number,
        active: !player.active,
      });
      setPlayers(prev => prev.map(p => p.id === player.id ? updated : p));
    } catch (err) {
      console.error('Failed to toggle active:', err);
    }
  };

  // Excel import — creates players in DB
  const handleExcel = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async evt => {
      const wb = XLSX.read(evt.target.result, { type: 'binary' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);

      const players = rows
        .filter(r => r['Naam'])
        .map(r => {
          const dobRaw = r['Geboortedatum'];
          let dob = null;
          if (dobRaw) {
            if (typeof dobRaw === 'number') {
              dob = new Date(Math.round((dobRaw - 25569) * 86400 * 1000)).toISOString().split('T')[0];
            } else {
              dob = String(dobRaw);
            }
          }
          return {
            name:          String(r['Naam']),
            squad_number:  r['RugN'] ? parseInt(r['RugN']) : null,
            position:      r['Pos'] ? String(r['Pos']) : null,
            nationality:   r['Nationaliteit'] ? String(r['Nationaliteit']) : null,
            dob,
            height:        r['Lengte'] ? parseFloat(r['Lengte']) : null,
            weight:        r['Gewicht'] ? parseFloat(r['Gewicht']) : null,
            dominant_foot: r['Voet'] ? String(r['Voet']) : 'Right',
            max_speed:     r['Max SPD'] ? parseFloat(r['Max SPD']) : null,
            photo_url:     r['Foto'] ? String(r['Foto']) : null,
            active:        r['Active'] === 'Y' || r['Active'] === true,
            notes:         null,
          };
        });

      // Import all players, continue even if one fails
      let success = 0;
      for (const p of players) {
        try {
          await createPlayer(p);
          success++;
        } catch (err) {
          console.warn('Skipped player:', p.name, err.message);
        }
      }
      console.log(`Imported ${success} of ${players.length} players`);
      await loadPlayers();
    };
    reader.readAsBinaryString(file);
  };

  const visible = players.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
                        String(p.squad_number).includes(search);
    const matchStatus = filter === 'all' ? true : filter === 'active' ? p.active : !p.active;
    const matchPos    = posFilter === 'All' ? true : p.position === posFilter;
    return matchSearch && matchStatus && matchPos;
  });

  const badge = (label, color) => (
    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: color + '22', color, fontWeight: 600 }}>{label}</span>
  );

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)' }}>
      Loading players...
    </div>
  );

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Player Database</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 2 }}>{players.filter(p => p.active).length} active · {players.filter(p => !p.active).length} non-active</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 8, border: '1px solid var(--border-strong)', cursor: 'pointer', fontSize: 14, color: 'var(--text-secondary)', background: 'var(--border)' }}>
            <Upload size={16} /> Import Excel
            <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleExcel} />
          </label>
          <button onClick={() => setModal('add')} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            <UserPlus size={16} /> Add Player
          </button>
        </div>
      </div>

      {players.length === 0 && (
        <div style={{ border: '2px dashed var(--border-strong)', borderRadius: 12, padding: 40, textAlign: 'center', marginBottom: 24, color: 'var(--text-muted)' }}>
          <Upload size={32} style={{ margin: '0 auto 12px', display: 'block', opacity: 0.5 }} />
          <p style={{ fontWeight: 600, marginBottom: 6 }}>Import your Excel file to get started</p>
          <p style={{ fontSize: 13 }}>Expected columns: Naam, RugN, Pos, Nationaliteit, Geboortedatum, Foto, Active</p>
        </div>
      )}

      {players.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or number..."
              style={{ width: '100%', padding: '9px 12px 9px 36px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--border)', color: 'var(--text-primary)', fontSize: 14, outline: 'none' }} />
          </div>
          {['active','inactive','all'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid var(--border-strong)', background: filter === f ? '#3b82f6' : 'var(--border)', color: filter === f ? '#fff' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontWeight: filter === f ? 600 : 400, textTransform: 'capitalize' }}>{f}</button>
          ))}
          <select value={posFilter} onChange={e => setPosFilter(e.target.value)}
            style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--border)', color: 'var(--text-secondary)', fontSize: 14, outline: 'none', cursor: 'pointer' }}>
            <option>All</option>
            {POSITIONS.map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
        {visible.map(p => (
          <div key={p.id} onClick={() => setModal(p)}
            style={{ background: 'var(--border)', borderRadius: 10, padding: 16, cursor: 'pointer', border: '1px solid var(--border-strong)', opacity: p.active ? 1 : 0.55, transition: 'border-color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <Avatar name={p.name} photoUrl={p.photo_url} size={44} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{p.position || '—'} {p.squad_number ? `· #${p.squad_number}` : ''}</div>
              </div>
              {badge(p.active ? 'Active' : 'Non-active', p.active ? '#10b981' : '#ef4444')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
              <span>🌍 {p.nationality || '—'}</span>
              <span>🎂 {p.dob ? p.dob.slice(0,10) : '—'}</span>
              <span>📏 {p.height ? `${p.height} cm` : '—'}</span>
              <span>⚖️ {p.weight ? `${fmtWeight(p.weight)} kg` : '—'}</span>
            </div>
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={e => { e.stopPropagation(); setInjuryModal(p); }}
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                <Stethoscope size={12} /> Injuries
              </button>
              <button onClick={e => { e.stopPropagation(); setDocumentsModal(p); }}
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                <FileText size={12} /> Docs
              </button>
              <button onClick={e => { e.stopPropagation(); toggleActive(p); }}
                style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'none', color: p.active ? '#ef4444' : '#10b981', cursor: 'pointer' }}>
                {p.active ? 'Set Non-active' : 'Set Active'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {modal && <PlayerModal player={modal === 'add' ? null : modal} onSave={savePlayer} onClose={() => setModal(null)} />}
      {injuryModal && <InjuryHistoryModal player={injuryModal} user={user} onClose={() => setInjuryModal(null)} />}
      {documentsModal && <DocumentsModal player={documentsModal} onClose={() => setDocumentsModal(null)} />}
    </div>
  );
}